import { forwardRef } from "react";

import { Stack, StackProps, SxProps, Theme, useTheme } from "@mui/material";

import { FormButton } from "@/forms-design-system/FormButton/FormButton";
import { FormIcon, FormIconProps } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { mergeSx } from "@/helpers/mergeSx";
import { Cross } from "@/icons/outlined";

export type FormInfoboxProps = StackProps & {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: "default" | "emphasis" | "error";
  components?: {
    icon?: {
      sx?: SxProps<Theme>;
      size: FormIconProps["size"];
    };
    title?: {
      sx?: SxProps<Theme>;
    };
    description?: {
      sx?: SxProps<Theme>;
    };
    callToActions?: {
      sx?: SxProps<Theme>;
    };
    dismissButton?: {
      sx?: SxProps<Theme>;
    };
  };
  callToActions?: React.ReactNode[];
  onDismiss?: () => void;
};

export const FormInfoboxRichIconClassName = "infobox-rich-icon";
export const FormInfoboxRichIconHighlightClassName = "infobox-rich-icon-highlight";
export const FormInfoboxRichIconSubtleClassName = "infobox-rich-icon-subtle";

export const FormInfobox = forwardRef<HTMLDivElement, FormInfoboxProps>(
  ({ icon, title, description, callToActions, variant = "default", components, sx, onDismiss, ...rest }, ref) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.infobox) {
      return <InvalidThemeErrorMessage />;
    }

    const styles = mergeSx(
      {
        width: "100%",
        boxSizing: "border-box",
        padding: theme.spacing(1.5, 2),
        borderRadius: theme.radius(1),

        ...(variant === "emphasis" && {
          backgroundColor: theme.palette.formsComponents.infobox.emphasis.backgroundColor,
          color: theme.palette.formsComponents.infobox.emphasis.textColor,
        }),

        ...(variant === "error" && {
          backgroundColor: theme.palette.formsComponents.infobox.error.backgroundColor,
          color: theme.palette.formsComponents.infobox.error.textColor,
        }),

        ...(variant === "default" && {
          backgroundColor: theme.palette.formsComponents.infobox.default.backgroundColor,
          color: theme.palette.formsComponents.infobox.default.textColor,
          [`& .${FormInfoboxRichIconClassName}`]: {
            fill: theme.palette.formsComponents.infobox.default.richIconFillBase,
          },
          [`& .${FormInfoboxRichIconHighlightClassName}`]: {
            fill: theme.palette.formsComponents.infobox.default.richIconFillHighlight,
          },
          [`& .${FormInfoboxRichIconSubtleClassName}`]: {
            fill: theme.palette.formsComponents.infobox.default.richIconFillSubtle,
          },
        }),
      },
      sx
    );

    const iconSize = components?.icon?.size ?? "medium";

    const iconColor = (() => {
      switch (variant) {
        case "default":
          return theme.palette.formsComponents.infobox.default.iconColor;
        case "emphasis":
          return theme.palette.formsComponents.infobox.emphasis.iconColor;
        case "error":
          return theme.palette.formsComponents.infobox.error.iconColor;
        default:
          return "currentColor";
      }
    })();

    const titleStyles = mergeSx(
      {
        ...(iconSize === "large" && {
          marginBlockStart: "2px", // For visual alignment with the icon
        }),
      },
      components?.title?.sx
    );

    const descriptionStyles = mergeSx(
      {
        ...(!title &&
          iconSize === "large" && {
            marginBlockStart: "2px", // For visual alignment with the icon
          }),
      },
      components?.description?.sx
    );

    return (
      <Stack ref={ref} sx={styles} {...rest}>
        <Stack direction="row" alignItems="flex-start" gap={1.5}>
          {icon && (
            <FormIcon color={iconColor} size={components?.icon?.size ?? "medium"} sx={components?.icon?.sx}>
              {icon}
            </FormIcon>
          )}
          <Stack flexGrow={1}>
            {title && (
              <FormTypography variant="subtitle2" sx={titleStyles}>
                {title}
              </FormTypography>
            )}
            {description && (
              <FormTypography variant="body2" sx={descriptionStyles}>
                {description}
              </FormTypography>
            )}
          </Stack>
          {onDismiss && (
            <FormButton
              variant="ghost"
              iconButton
              startIcon={<Cross />}
              onClick={onDismiss}
              sx={mergeSx({ flexShrink: 0 }, components?.dismissButton?.sx)}
            />
          )}
        </Stack>
        {callToActions && (
          <Stack
            direction="row-reverse"
            justifyContent="flex-start"
            gap={1.5}
            marginBlockStart={1.5}
            sx={components?.callToActions?.sx}
          >
            {callToActions}
          </Stack>
        )}
      </Stack>
    );
  }
);

FormInfobox.displayName = "FormInfobox";
